/**
 * BottomNav — floating glass tab bar with motion-driven active pill.
 *
 * The active tab is highlighted by a layoutId-shared pill that
 * smoothly slides between positions when the user navigates. This
 * is the same trick the iOS 26 segmented control uses — the pill
 * is one DOM node that physically moves between tab cells, so the
 * spring animation feels natural.
 */
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';
import iconSet from '@/assets/nav-set.webp';
import iconSlots from '@/assets/nav-slots.webp';
import iconTree from '@/assets/nav-tree.webp';
import iconStart from '@/assets/nav-start.webp';
import iconWallet from '@/assets/nav-wallet.webp';

interface Tab {
  to: string;
  labelKey: string;
  icon: string;
  end?: boolean;
}

const tabs: Tab[] = [
  { to: '/', labelKey: 'nav.networkTab', icon: iconSet, end: true },
  { to: '/slots', labelKey: 'nav.slotsTab', icon: iconSlots },
  { to: '/network', labelKey: 'nav.treeTab', icon: iconTree },
  { to: '/quick', labelKey: 'nav.quickStartTab', icon: iconStart },
  { to: '/wallet', labelKey: 'nav.walletTab', icon: iconWallet },
];

export function BottomNav() {
  const location = useLocation();
  const { haptic } = useTelegramWebApp();
  const sounds = useUISounds();
  const { t } = useTranslation();

  return (
    <nav
      aria-label={t('nav.ariaMain')}
      className="fixed inset-x-0 bottom-0 z-30 flex justify-center pb-[max(env(safe-area-inset-bottom),8px)] pt-2 px-4 pointer-events-none"
    >
      <div className="lg-pill has-warp-layer relative pointer-events-auto flex items-center gap-1 px-2 py-1 max-w-md w-full justify-between overflow-hidden">
        {/* Refraction layer — empty div with backdrop-filter blur AND
            filter: url(#lg-glass-warp). The SVG displacement map
            warps the rendered backdrop into an iPhone-style lens. */}
        <div className="lg-glass-warp-layer" aria-hidden />
        {tabs.map(({ to, labelKey, icon, end }) => {
          const isActive = end
            ? location.pathname === to
            : location.pathname.startsWith(to);
          return (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-tour={to === '/' ? 'nav-home' : `nav-${to.slice(1)}`}
              onClick={() => { haptic.selection(); sounds.navTab(); }}
              data-no-sound="true"
              className={cn(
                'relative z-10 flex flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5',
                'rounded-[var(--radius-pill)]',
                'text-[9px] tracking-[0.05em] uppercase font-medium',
                'transition-colors duration-300',
                isActive
                  ? 'text-[#0a0a0f]'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]',
              )}
              style={{ minHeight: 44 }}
            >
              {isActive && (
                <motion.span
                  layoutId="bottom-nav-pill"
                  aria-hidden
                  className="absolute inset-0 rounded-[var(--radius-pill)]"
                  style={{
                    background:
                      'linear-gradient(180deg, var(--accent-primary), color-mix(in oklab, var(--accent-primary) 70%, var(--accent-secondary)))',
                    boxShadow:
                      '0 12px 32px -8px color-mix(in oklab, var(--accent-primary) 60%, transparent), inset 0 1px 0 0 rgba(255,255,255,0.5)',
                  }}
                  transition={{ type: 'spring', stiffness: 480, damping: 36 }}
                />
              )}
              <img
                src={icon}
                alt=""
                aria-hidden
                className="relative z-10 h-7 w-7 object-contain"
                style={{
                  // Активная — полная насыщенность (выделяем фокус).
                  // Неактивные — saturate(0.3) (приглушённый бежевый).
                  filter: isActive
                    ? 'brightness(0.7) saturate(1) contrast(1.1)'
                    : 'saturate(0.3) drop-shadow(0 0 4px rgba(255,160,50,0.2))',
                  transition: 'filter 0.3s',
                }}
              />
              <span className="relative z-10">{t(labelKey)}</span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
